import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {Observable} from 'rxjs';
import {LOCAL_STORAGE_SELECTED_PROFILE_KEY, ProfilesFacadeService} from "./profiles-facade.service";
import {map, take, tap} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {StoreState} from "../redux/reducers";
import {ProfilesSelectedAction} from "../redux/actions/profiles.action";
import {AdminFacadeService} from '../admin/admin-facade.service';
import {StorageService} from '../services/storage.service';
import {AuthenticationService} from '@services/authentication.service';
import { Profile } from '@model/profile';

@Injectable({
  providedIn: 'root'
})
export class ProfileGuard  {

  constructor(private _profileFacade: ProfilesFacadeService,
              private _adminFacade: AdminFacadeService,
              private _authenticationService: AuthenticationService,
              private _store: Store<StoreState>,
              private _localStorage: StorageService,
              private _router: Router
  ) {
  }

  canActivate(
    nextRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    if (this._authenticationService.isSuperAdmin()) {
      return true;
    }

    return this._profileFacade.profiles$.pipe(
      take(1),
      map(profiles => {
        if (profiles.length === 0) {
          this._router.navigate(['noprofiles']);
          return false;
        }
        const {profileType, schoolId} = this.getProfileIdAndSchoolIdFromQueryParams(nextRoute.queryParams);
        if (profileType && schoolId) {
          return this.getProfileAndOrganizationFromQueryParams(profiles, profileType, schoolId);
        } else {
          return this.getProfileAndOrganizationFromLocalStorage(profiles);
        }        
      }),
      tap(decision => decision && this._adminFacade.loadSchool())
    );
  }

  private getProfileIdAndSchoolIdFromQueryParams(queryParams: {}): { profileType: string, schoolId: string } {
    const profileType = queryParams['profileType'];
    const schoolId = queryParams['schoolId'];
    return {profileType, schoolId};
  }

  private getProfileAndOrganizationFromQueryParams(profiles: Profile[], profileType: string, schoolId: string) {
    const profile = profiles.find(p => p.type === profileType && p.schoolId === schoolId);
    if (profile) {
      this._localStorage.setItem(LOCAL_STORAGE_SELECTED_PROFILE_KEY, profile.id.toString());
      this._store.dispatch(new ProfilesSelectedAction(profile));
      return true;
    } else {
      this._router.navigate(['profiles'], {queryParams: {noMatching: true}});
      return false;
    }
  }

  private getProfileAndOrganizationFromLocalStorage(profiles: Profile[]) {
    if (profiles.length === 1) {
      console.info('only one profile loaded');
      this._localStorage.setItem(LOCAL_STORAGE_SELECTED_PROFILE_KEY, profiles[0].id.toString());
      this._store.dispatch(new ProfilesSelectedAction(profiles[0]));
      return true;
    }
    else {
      console.info('multiple profiles loaded from backend');
      const profileIdFromLocalStorage = this._localStorage.getItem(LOCAL_STORAGE_SELECTED_PROFILE_KEY);
      const profileFromLocalStorage = profiles.find(p => p.id.toString() === profileIdFromLocalStorage);
      if (profileFromLocalStorage) {
        console.info('profile selected from localstorage');
        this._store.dispatch(new ProfilesSelectedAction(profileFromLocalStorage));
        return true;
      } else {
        console.info('no profile selected in localstorage');
        this._router.navigate(['profiles']);
        return false;
      }
    }
  }
}
